import { Organization } from "src/domains/organization/types/OrganizationTypes";
import { StorageKeys } from "src/platform/constants/Storage";
import debugLogger from "src/platform/utils/debugLogger";

/**
 * Helper functions for managing organization display data in localStorage.
 *
 * NOTE: Token and org alias persistence is now handled by OrgSessionManager.
 * These helpers only manage display metadata (org name/description for sidebar).
 */

/**
 * Get the current organization display data from localStorage
 * @returns The current organization or null if not found
 */
export const getCurrentOrganization = (): Organization | null => {
  try {
    const orgJSON = localStorage.getItem(StorageKeys.SELECTED_ORGANIZATION);
    if (!orgJSON) return null;

    return JSON.parse(orgJSON);
  } catch (error) {
    debugLogger.error("Error retrieving current organization:", error);
    return null;
  }
};

/**
 * @deprecated Use `orgSessionManager.selectOrg()` instead.
 * This function only persists display metadata (org name/description).
 * Token and alias persistence is now handled atomically by OrgSessionManager.
 *
 * Set the current organization display data in localStorage
 * @param organization The organization to set as current
 */
export const setCurrentOrganization = (organization: Organization): void => {
  try {
    // Save the full organization object for display purposes only
    localStorage.setItem(
      StorageKeys.SELECTED_ORGANIZATION,
      JSON.stringify(organization),
    );

    // Update recent organizations list
    updateRecentOrganizations(organization);
  } catch (error) {
    debugLogger.error("Error setting current organization:", error);
  }
};

/**
 * Update the list of recent organizations
 * @param organization The organization to add to recent list
 */
export const updateRecentOrganizations = (organization: Organization): void => {
  try {
    const recentOrgsStr = localStorage.getItem(
      StorageKeys.RECENT_ORGANIZATIONS,
    );
    const recentOrgs = recentOrgsStr ? JSON.parse(recentOrgsStr) : [];

    // Remove if already exists
    const filteredOrgs = recentOrgs.filter(
      (org: Organization) => org.alias !== organization.alias,
    );

    // Add to beginning of array
    filteredOrgs.unshift(organization);

    // Keep only last 10 organizations
    const trimmedOrgs = filteredOrgs.slice(0, 10);

    localStorage.setItem(
      StorageKeys.RECENT_ORGANIZATIONS,
      JSON.stringify(trimmedOrgs),
    );
  } catch (error) {
    debugLogger.error("Error updating recent organizations:", error);
  }
};

/**
 * Get the list of recent organizations
 * @returns Array of recent organizations
 */
export const getRecentOrganizations = (): Organization[] => {
  try {
    const recentOrgsStr = localStorage.getItem(
      StorageKeys.RECENT_ORGANIZATIONS,
    );
    return recentOrgsStr ? JSON.parse(recentOrgsStr) : [];
  } catch (error) {
    debugLogger.error("Error retrieving recent organizations:", error);
    return [];
  }
};

/**
 * @deprecated Use `orgSessionManager.clearSession()` instead.
 * Clear all organization display data from localStorage.
 */
export const clearOrganizationData = (): void => {
  try {
    localStorage.removeItem(StorageKeys.SELECTED_ORGANIZATION);
    localStorage.removeItem(StorageKeys.RECENT_ORGANIZATIONS);
  } catch (error) {
    debugLogger.error("Error clearing organization data:", error);
  }
};
